import type { TaxonomyClassFields } from "@sitescene/types";

import { useState } from "react";

import { Button, Field, Select, TextArea, TextInput } from "@/components/field";

import { ListInput } from "./ListInput";
import { CLASS_ID_PATTERN } from "./taxonomy-view";

interface ClassEditorProps {
	busy: boolean;
	/** Absent when creating: the id is typed in the form. */
	classId?: string;
	classOptions: { label: string; value: string }[];
	error: Error | null;
	/**
	 * What the form starts from when that differs from `value`, the baseline it
	 * is compared against — a suggestion to review, say. Defaults to `value`.
	 */
	initial?: { classId?: string; fields: TaxonomyClassFields };
	/** Fires as Alertable is toggled, before the class is saved. */
	onAlertableChange?: (alertable: boolean) => void;
	onRevert?: () => void;
	onSave: (classId: string, fields: TaxonomyClassFields) => void;
	staged: boolean;
	value: TaxonomyClassFields;
}

const probability = (logOdds: number) => 1 / (1 + Math.exp(-logOdds));

/**
 * Every field of one class. Edits stay local until saved to the draft; the
 * parent remounts it (by `key`) to start over from another value.
 */
export const ClassEditor = ({
	busy,
	classId,
	classOptions,
	error,
	initial,
	onAlertableChange,
	onRevert,
	onSave,
	staged,
	value,
}: ClassEditorProps) => {
	const creating = classId === undefined;
	const [id, setId] = useState(classId ?? initial?.classId ?? "");
	const [fields, setFields] = useState(initial?.fields ?? value);
	const patch = (changes: Partial<TaxonomyClassFields>) =>
		setFields((current) => ({ ...current, ...changes }));

	const others = classOptions.filter((option) => option.value !== id);
	const idValid = !creating || CLASS_ID_PATTERN.test(id);
	const { falseNegative, falsePositive } = fields.costs;
	const costsValid = falseNegative > 0 && falsePositive > 0;
	// An active class with no description can never be chosen well.
	const described = !fields.active || Boolean(fields.description?.trim());
	const valid =
		idValid && fields.displayName.trim() !== "" && costsValid && described;
	const dirty = creating || JSON.stringify(fields) !== JSON.stringify(value);
	const threshold = costsValid
		? falsePositive / (falsePositive + falseNegative)
		: undefined;
	const prefix = `class-${classId ?? "new"}`;

	return (
		<form
			className="flex flex-col gap-3"
			onSubmit={(event) => {
				event.preventDefault();
				if (valid) onSave(id.trim(), fields);
			}}
		>
			<div className="flex flex-wrap items-end gap-3">
				<Field htmlFor={`${prefix}-id`} label="Class id">
					<TextInput
						disabled={!creating}
						id={`${prefix}-id`}
						onChange={(event) => setId(event.target.value.trim())}
						placeholder="weapon.knife"
						value={id}
					/>
				</Field>
				<Field htmlFor={`${prefix}-name`} label="Display name">
					<TextInput
						id={`${prefix}-name`}
						onChange={(event) => patch({ displayName: event.target.value })}
						value={fields.displayName}
					/>
				</Field>
				<label className="flex items-center gap-2 pb-1 text-sm">
					<input
						checked={fields.alertable}
						onChange={(event) => {
							patch({ alertable: event.target.checked });
							onAlertableChange?.(event.target.checked);
						}}
						type="checkbox"
					/>
					<span className="text-primary">Alertable</span>
				</label>
				<label className="flex items-center gap-2 pb-1 text-sm">
					<input
						checked={fields.active}
						onChange={(event) => patch({ active: event.target.checked })}
						type="checkbox"
					/>
					<span className="text-primary">Active</span>
				</label>
			</div>
			{creating && id && !idValid && (
				<p className="text-error-primary text-sm" role="alert">
					Class ids are family.member in lowercase kebab-case, and can never
					change once published.
				</p>
			)}

			<Field
				htmlFor={`${prefix}-description`}
				label="Description (the classifier routes alerts by it and the blind pass names classes by it)"
			>
				<TextArea
					id={`${prefix}-description`}
					onChange={(event) => patch({ description: event.target.value })}
					rows={2}
					value={fields.description ?? ""}
				/>
			</Field>

			{!described && (
				<p className="text-error-primary text-sm" role="alert">
					An active class needs a description: the classifier picks an alert's
					class from it.
				</p>
			)}

			<Field htmlFor={`${prefix}-confusers`} label="Confusers">
				<ListInput
					id={`${prefix}-confusers`}
					onChange={(confusers) => patch({ confusers })}
					options={others}
					values={fields.confusers}
				/>
			</Field>

			<div className="flex flex-wrap items-end gap-3">
				<Field htmlFor={`${prefix}-fn`} label="Cost of a miss">
					<TextInput
						id={`${prefix}-fn`}
						min={0}
						onChange={(event) =>
							patch({
								costs: {
									...fields.costs,
									falseNegative: Number(event.target.value),
								},
							})
						}
						step="any"
						type="number"
						value={falseNegative}
					/>
				</Field>
				<Field htmlFor={`${prefix}-fp`} label="Cost of a false alarm">
					<TextInput
						id={`${prefix}-fp`}
						min={0}
						onChange={(event) =>
							patch({
								costs: {
									...fields.costs,
									falsePositive: Number(event.target.value),
								},
							})
						}
						step="any"
						type="number"
						value={falsePositive}
					/>
				</Field>
				<Field htmlFor={`${prefix}-prior`} label="Default log prior odds">
					<TextInput
						id={`${prefix}-prior`}
						onChange={(event) =>
							patch({ defaultLogPriorOdds: Number(event.target.value) })
						}
						step="any"
						type="number"
						value={fields.defaultLogPriorOdds}
					/>
				</Field>
				<Field htmlFor={`${prefix}-parent`} label="Parent">
					<Select
						id={`${prefix}-parent`}
						onChange={(event) =>
							patch({ parent: event.target.value || undefined })
						}
						value={fields.parent ?? ""}
					>
						<option value="">none</option>
						{others.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</Select>
				</Field>
			</div>
			<p className="text-tertiary text-xs">
				Prior ≈ {(probability(fields.defaultLogPriorOdds) * 100).toPrecision(2)}
				%
				{threshold !== undefined &&
					` · alerts at ≥ ${(threshold * 100).toPrecision(2)}% belief`}
			</p>

			<div className="flex flex-wrap items-center gap-2">
				<Button disabled={busy || !valid || !dirty} type="submit">
					{creating ? "Add class to draft" : "Save class to draft"}
				</Button>
				{staged && onRevert && (
					<Button
						disabled={busy}
						onClick={onRevert}
						type="button"
						variant="danger"
					>
						Remove from draft
					</Button>
				)}
				{error && (
					<span className="text-error-primary text-sm" role="alert">
						{error.message}
					</span>
				)}
			</div>
		</form>
	);
};
